<script setup lang="ts">
import { useAttrs } from "vue";
import Icon from "../Icon/Icon.vue";

type CheckboxProps = {
  error?: string | null;
  disabled?: boolean;
};

const props = defineProps<CheckboxProps>();

const isChecked = defineModel<boolean>("checked");

const attrs = useAttrs();

const toggleChecked = () => {
  if (!props.disabled) {
    isChecked.value = !isChecked.value;
  }
};
</script>

<template>
  <label :class="{ disabled }">
    <input
      v-bind="attrs"
      :checked="isChecked"
      type="checkbox"
      :aria-describedby="error ? `${attrs.name}-error` : undefined"
      :disabled="disabled"
      class="checkbox-input"
      tabindex="-1"
      @change="toggleChecked"
    />
    <Icon
      :name="isChecked ? 'faSquareCheck' : 'farSquare'"
      v-focus
      tabindex="0"
      :class="['checkbox-icon', { error }]"
      @click="toggleChecked"
      @keydown.enter.prevent="toggleChecked"
    />

    <slot />
  </label>
  <span
    v-if="error"
    :id="`${attrs.name}-error`"
    class="error-text"
    role="alert"
  >
    <Icon name="faBan" /> {{ error }}
  </span>
</template>

<style lang="scss" scoped>
.checkbox-input {
  position: absolute;
  left: 1px;
  height: 20px;
  width: 20px;
  opacity: 0;

  &:hover + .checkbox-icon {
    background-color: var(--clr-p2);
  }
  &:not:disabled {
    cursor: pointer;
  }
  &:disabled {
    cursor: not-allowed;
  }
}

.checkbox-icon {
  padding: 3px 3px 3px 4px;
  border-radius: 4px;
  color: var(--clr-p7);

  &.error {
    color: var(--clr-e200);
  }
}

label {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  width: fit-content;
  cursor: pointer;
}

.error-text {
  display: flex;
  gap: 4px;
  color: var(--clr-e300);
}

.disabled {
  cursor: not-allowed;

  .checkbox-input:focus + .checkbox-icon,
  .checkbox-input:hover + .checkbox-icon {
    background-color: unset;
  }
}
</style>
